import { DestroyRef, Inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { CmsContainer } from '@domgen/dgx-fe-cms';
import { determineIsDesktop, filterNullUndefined } from '@domgen/dgx-fe-common';
import { ModalService } from '@domgen/dgx-fe-components/modal';
import { DOMAIN_CONFIG, DomainConfig } from '@domgen/dgx-fe-config';
import { GAService } from '@domgen/dgx-fe-ga';
import { TranslateService } from '@domgen/dgx-fe-i18n';
import { ComponentStore } from '@ngrx/component-store';
import {
  RegistrationFacade,
  RegistrationService,
} from '@us-registration/data-access';
import {
  CheckoutAuthResponse,
  CheckoutBasket,
  CmsProtectionDetailsPage,
  CmsProtectionDetailsUpsellModal,
  DiscountContent,
  PaymentFrequency,
  PropositionType,
  ProtectionDetailsPaymentOption,
  ProtectionDetailsUpsellModalData,
  ProtectionDetailsUpsellModalDataWib,
  ProtectionDetailsUpsellModalResult,
  RegisteredItem,
  RegisteredItemProductDetails,
  REGISTRATION_CONFIG,
  RegistrationApplianceDetails,
  RegistrationConfig,
  RegistrationRequest,
  RegistrationRoutes,
  RegistrationSystemCode,
} from '@us-registration/util-models';
import posthog from 'posthog-js';
import { combineLatest, map, Observable, of, switchMap, take, tap } from 'rxjs';
import { ProtectionDetailsUpsellModalComponent } from '../../components/protection-details-upsell-modal/protection-details-upsell-modal.component';
import {
  constructOffer,
  getCustomerDetails,
  getDiscountDetails,
} from '../../utils/protection-details.utils';

export interface ViewModel {
  cmsData: CmsProtectionDetailsPage;
  ctaUpsellLoading: boolean;
  discountContent?: DiscountContent;
  hasRegisteredItemOutOfGuarantee: boolean;
  isDesktop: boolean;
  annualPlansEnabled: boolean;
  hasDiscountPrice: boolean;
  protectionDetailsUpsellModalData?: ProtectionDetailsUpsellModalData;
  registeredItem?: RegisteredItem;
  quoteHasAnnualPayment: boolean;
  registeredItemProductDetails?: RegisteredItemProductDetails;
  merchandiseBrand?: string;
  prepayPrice?: number;
  subscriptionPrice?: number;
  subscriptionDiscountPrice?: number;
  applianceType?: string;
  protectionDetailsPaymentOptions?: ProtectionDetailsPaymentOption[];
}

export interface ComponentState {
  ctaUpsellLoading: boolean;
  registeredItem?: RegisteredItem;
  registrationRequest?: RegistrationRequest;
  systemCode: RegistrationSystemCode;
  applianceType?: string;
}

@Injectable()
export class ProtectionDetailsComponentService extends ComponentStore<ComponentState> {
  checkoutInit$ = combineLatest([
    this.registrationFacade.quoteResponse$,
    this.registrationFacade.registrationCustomerDetails$,
  ]);

  private readonly ctaUpsellLoading$: Observable<boolean> = this.select(
    (state) => state.ctaUpsellLoading
  );

  private readonly applianceType$: Observable<string | undefined> = this.select(
    (state) => state.applianceType
  );

  private readonly cmsProtectionDetailsUpsellModal$: Observable<CmsProtectionDetailsUpsellModal> =
    combineLatest([this.cmsContainer.content$]).pipe(
      map(([cmsData]) => ({
        protectionDetailsModalTitle: cmsData.protectionDetailsModalTitle,
        protectionDetailsModalSubtitle: cmsData.protectionDetailsModalSubtitle,
        protectionDetailsModalBenefitsListTitle:
          cmsData.protectionDetailsModalBenefitsListTitle,
        protectionDetailsModalBenefitsListItems:
          cmsData.protectionDetailsModalBenefitsListItems,
      }))
    );

  protectionDetailsUpsellModalData$: Observable<ProtectionDetailsUpsellModalDataWib> =
    combineLatest({
      cmsProtectionDetailsUpsellModal: this.cmsProtectionDetailsUpsellModal$,
      merchandiseBrand: this.domainConfig.clientName,
      annualPlansEnabled: of(this.annualPlansEnabled),
      hasDiscountPrice: this.registrationFacade.hasDiscountPrice$,
      registeredItemHasAnnualPayment:
        this.registrationFacade.quoteHasAnnualPayment$,
      registeredItemProductPriceDetails:
        this.registrationFacade.quoteNetTotalAmount$,
      registeredItemAnnualProductDetails:
        this.registrationFacade.registeredItemAnnualProductDetails$,
      registeredItemProducts: this.registrationFacade.registeredItemProducts$,
      systemCode: this.registrationFacade.registrationSystemCode$,
      protectionDetailsPaymentOptions:
        this.registrationFacade.protectionDetailsPaymentOptions$,
      prepayPrice: this.registrationFacade.protectionDetailsPrepayPrice$,
      subscriptionPrice:
        this.registrationFacade.protectionDetailsSubscriptionPrice$,
      discountContent: this.getDiscountContentFromQuote(),
    });

  readonly vm$: Observable<ViewModel> = combineLatest({
    applianceType: this.applianceType$,
    cmsData: this.cmsContainer.content$,
    ctaUpsellLoading: this.ctaUpsellLoading$,
    discountContent: this.getDiscountContentFromQuote(),
    isDesktop: determineIsDesktop(),
    hasRegisteredItemOutOfGuarantee:
      this.registrationFacade.hasRegisteredItemOutOfGuarantee$,
    merchandiseBrand: of(this.domainConfig.clientName),
    annualPlansEnabled: of(this.annualPlansEnabled),
    price: this.registrationFacade.quoteNetTotalAmount$,
    hasDiscountPrice: this.registrationFacade.hasDiscountPrice$,
    registeredItemProductDetails:
      this.registrationFacade.registeredItemProductDetails$,
    quoteHasAnnualPayment: this.registrationFacade.quoteHasAnnualPayment$,
    protectionDetailsPaymentOptions:
      this.registrationFacade.protectionDetailsPaymentOptions$,
    prepayPrice: this.registrationFacade.protectionDetailsPrepayPrice$,
    subscriptionPrice:
      this.registrationFacade.protectionDetailsSubscriptionPrice$,
    subscriptionDiscountPrice:
      this.registrationFacade.subscriptionDiscountPrice$,
  });

  constructor(
    @Inject(REGISTRATION_CONFIG)
    private readonly registrationConfig: RegistrationConfig,
    @Inject(DOMAIN_CONFIG) private readonly domainConfig: DomainConfig,
    private readonly cmsContainer: CmsContainer<CmsProtectionDetailsPage>,
    private readonly destroyRef: DestroyRef,
    private readonly modalService: ModalService,
    private readonly registrationFacade: RegistrationFacade,
    private readonly router: Router,
    private readonly registrationService: RegistrationService,
    private readonly gaService: GAService,
    private readonly translateService: TranslateService
  ) {
    super({
      ctaUpsellLoading: false,
      systemCode: RegistrationSystemCode.InternetProductRegistration,
    });
  }

  private readonly updateApplianceType = this.updater(
    (state: ComponentState, applianceType: string | undefined) => ({
      ...state,
      applianceType,
    })
  )(
    this.registrationFacade.registrationRequest$.pipe(
      filterNullUndefined(),
      map((registrationRequest) => registrationRequest.merchandise[0].type)
    )
  );

  private readonly updateRegisteredItem = this.updater(
    (
      state: ComponentState,
      registrationRequest: RegistrationRequest | undefined
    ) => ({
      ...state,
      registrationRequest,
    })
  )(this.registrationFacade.registrationRequest$);

  private readonly updateSystemCode = this.updater(
    (state: ComponentState, systemCode: RegistrationSystemCode) => ({
      ...state,
      systemCode,
    })
  )(this.registrationFacade.registrationSystemCode$);

  triggerRegistrationUpsellModal() {
    this.protectionDetailsUpsellModalData$
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        switchMap(
          (
            protectionDetailsUpsellModalData: ProtectionDetailsUpsellModalDataWib
          ) =>
            this.openProtectionDetailsUpsellModal(
              protectionDetailsUpsellModalData
            )
        ),
        filterNullUndefined(),
        tap((result: ProtectionDetailsUpsellModalResult) => {
          if (result === ProtectionDetailsUpsellModalResult.RegistrationOnly) {
            this.continueToRegistrationOnly();
          }

          if (result === ProtectionDetailsUpsellModalResult.ExtraProtection) {
            this.continueToCheckout();
          }

          if (
            result === ProtectionDetailsUpsellModalResult.ExtraProtectionPrepay
          ) {
            this.continueToCheckout(true);
          }
        })
      )
      .subscribe();
  }

  handlePaymentOptionAction(clickedPrepayCta: boolean): void {
    this.continueToCheckout(clickedPrepayCta);
  }

  continueToCheckout(clickedPrepayCta = false): void {
    this.constructCheckoutInitialisationPayload(clickedPrepayCta);

    posthog.capture('protection-details-upsell-cta-clicked');

    this.updateCtaUpsellLoading(true);

    if (this.registeredItemAppliance) {
      this.gaService.tag({
        pagename: 'protection-details',
        event: 'add_to_cart',
        ecommerce: {
          items: [
            {
              item_brand: this.registeredItemAppliance.brand,
              item_category: this.registeredItemAppliance.type,
              appliance_type: this.registeredItemAppliance.type,
              appliance_purchase_cost:
                this.registeredItemAppliance.purchasePrice,
              appliance_purchase_date:
                this.registeredItemAppliance.purchaseDate,
              appliance_warranty: true,
              item_id: '',
              item_name: `${this.registeredItemAppliance.brand} ${this.registeredItemAppliance.type}`,
              price: this.registeredItemAppliance.purchasePrice,
              quantity: 1,
            },
          ],
        },
      });
    }
  }

  private readonly updateCtaUpsellLoading = this.updater(
    (state: ComponentState, ctaUpsellLoading: boolean) => ({
      ...state,
      ctaUpsellLoading,
    })
  );

  continueToRegistrationOnly(): void {
    posthog.capture('protection-details-upsell-cta-registration-only');
    this.router.navigateByUrl(RegistrationRoutes.RegistrationConfirmation);
  }

  openProtectionDetailsUpsellModal(
    protectionDetailsUpsellModalData: ProtectionDetailsUpsellModalDataWib
  ): Observable<ProtectionDetailsUpsellModalResult | undefined> {
    return this.modalService.open<
      ProtectionDetailsUpsellModalComponent,
      ProtectionDetailsUpsellModalDataWib,
      ProtectionDetailsUpsellModalResult
    >(ProtectionDetailsUpsellModalComponent, {
      data: {
        cmsProtectionDetailsUpsellModal:
          protectionDetailsUpsellModalData.cmsProtectionDetailsUpsellModal,
        merchandiseBrand: this.domainConfig.clientName,
        annualPlansEnabled: protectionDetailsUpsellModalData.annualPlansEnabled,
        prepayPrice: protectionDetailsUpsellModalData.prepayPrice,
        hasDiscountPrice: protectionDetailsUpsellModalData.hasDiscountPrice,
        protectionDetailsPaymentOptions:
          protectionDetailsUpsellModalData.protectionDetailsPaymentOptions,
        registeredItemHasAnnualPayment:
          protectionDetailsUpsellModalData.registeredItemHasAnnualPayment,
        registeredItemProductPriceDetails:
          protectionDetailsUpsellModalData.registeredItemProductPriceDetails,
        registeredItemAnnualProductDetails:
          protectionDetailsUpsellModalData.registeredItemAnnualProductDetails,
        registeredItemProducts:
          protectionDetailsUpsellModalData.registeredItemProducts,
        subscriptionPrice: protectionDetailsUpsellModalData.subscriptionPrice,
        systemCode: protectionDetailsUpsellModalData.systemCode,
        discountContent: protectionDetailsUpsellModalData.discountContent,
      },
      maxWidth: 'max-content',
      maxHeight: 'max-content',
    }).afterClosed$;
  }

  get registeredItemAppliance(): RegistrationApplianceDetails | undefined {
    return this.get((state) => state.registrationRequest?.merchandise[0]);
  }

  get systemCode(): RegistrationSystemCode {
    return this.get((state) => state.systemCode);
  }

  get annualPlansEnabled(): boolean {
    return Boolean(this.registrationConfig.features.annualPlans);
  }

  constructCheckoutInitialisationPayload(clickedPrepayCta: boolean) {
    this.registrationFacade.hasInitialisedCheckout$
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        take(1),
        switchMap((checkoutInit: boolean) =>
          checkoutInit
            ? this.router.navigate(['/checkout'])
            : this.getFirstInitialisationPayload(clickedPrepayCta)
        )
      )
      .subscribe();
  }

  getFirstInitialisationPayload(clickedPrepayCta: boolean) {
    return this.registrationService.getCheckoutToken().pipe(
      switchMap((checkoutAuthResponse: CheckoutAuthResponse) =>
        this.checkoutInit$.pipe(
          map(([quote, customer]) => {
            if (quote?.offer) {
              const checkoutBasket = {} as CheckoutBasket;
              const offer = constructOffer(quote, clickedPrepayCta);

              checkoutBasket.offers = [];
              checkoutBasket.offers.push(offer);
              checkoutBasket.channelSourceData = {
                checkoutSourceRef: 'whirlpool_client_portal',
              };

              this.registrationFacade.addPaymentFrequency(
                clickedPrepayCta
                  ? PaymentFrequency.Annual
                  : PaymentFrequency.Monthly
              );

              this.registrationFacade.addCheckoutInitialisation({
                authToken: checkoutAuthResponse.externalApiToken,
                basket: {
                  ...checkoutBasket,
                },
                customer: getCustomerDetails(customer, quote),
                proposition: {
                  type: clickedPrepayCta
                    ? PropositionType.AnnualPrepay
                    : PropositionType.MonthlySubs,
                },
              });
              this.router.navigate(['/checkout']);
            }
            return;
          })
        )
      )
    );
  }

  getDiscountContentFromQuote() {
    return this.registrationFacade.quoteResponse$.pipe(
      switchMap((quote) => {
        if (!quote) {
          return of(undefined);
        }

        const discountContent = getDiscountDetails(quote);

        if (!discountContent) {
          return of(undefined);
        }

        return of(discountContent);
      })
    );
  }
}
